// src/hooks/useAcademicData.js
import { useState, useEffect, useCallback } from 'react';
import api from './../../../api';

export const useAcademicData = (tenantId, includeCompleted = false) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [activeAcademicYears, setActiveAcademicYears] = useState([]);
  const [completedAcademicYears, setCompletedAcademicYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [semesters, setSemesters] = useState([]);
  const [terms, setTerms] = useState([]);
  const [isSemesterBased, setIsSemesterBased] = useState(false);
  const [loadingContexts, setLoadingContexts] = useState(false);

  const log = useCallback((message, data) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📚 [useAcademicData] ${message}`, data || '');
    }
  }, []);

  const fetchAllAcademicYears = useCallback(async () => {
    if (!tenantId) {
      log('No tenantId provided');
      return;
    }

    log('Fetching academic years for tenant:', tenantId);
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch active academic years
      const activeRes = await api.get(
        `/api/academic-year/all-active?tenantId=${tenantId}`,
        { headers }
      );

      let activeYears = activeRes?.data?.academicYears || 
                       activeRes?.data?.activeAcademicYears || 
                       activeRes?.data || [];
      
      if (!Array.isArray(activeYears) && activeYears) {
        activeYears = [activeYears];
      }

      log('Active years fetched:', activeYears.length);

      // Fetch completed academic years if needed
      let completedYears = [];
      if (includeCompleted) {
        const completedRes = await api.get(
          `/api/academic-year/all-completed?tenantId=${tenantId}`,
          { headers }
        );
        
        completedYears = completedRes?.data?.academicYears || 
                        completedRes?.data || [];
        
        if (!Array.isArray(completedYears) && completedYears) {
          completedYears = [completedYears];
        }
        
        log('Completed years fetched:', completedYears.length);
      }

      // Process years to ensure consistent format
      const processYears = (years) => {
        return years.map(ay => ({
          ...ay,
          _id: ay._id || ay.id,
          semesters: ay.semesters || [],
          terms: ay.terms || [],
          isSemesterBased: ay.isSemesterBased || false,
          status: ay.status || 'active'
        }));
      };

      const processedActive = processYears(activeYears);
      const processedCompleted = processYears(completedYears);

      setActiveAcademicYears(processedActive);
      setCompletedAcademicYears(processedCompleted);
      setAcademicYears([...processedActive, ...processedCompleted]);

    } catch (err) {
      console.error('Error fetching academic years:', err);
      setError(err.message);
      log('Error fetching years:', err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, includeCompleted, log]);

  const fetchSemesters = useCallback(async (academicYearId) => {
    if (!academicYearId) return [];
    
    try {
      setLoadingContexts(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await api.get(`/api/classManager/semesters/${academicYearId}`, { headers });
      const fetchedSemesters = res.data.semesters || [];
      setSemesters(fetchedSemesters);
      log(`Fetched ${fetchedSemesters.length} semesters for AY ${academicYearId}`);
      return fetchedSemesters;
    } catch (err) {
      console.warn('Error fetching semesters:', err);
      setSemesters([]);
      return [];
    } finally {
      setLoadingContexts(false);
    }
  }, [log]);

  const fetchTerms = useCallback(async (academicYearId) => {
    if (!academicYearId || !tenantId) return [];
    
    try {
      setLoadingContexts(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await api.get(
        `/api/academic-year/${academicYearId}/terms?tenantId=${tenantId}`,
        { headers }
      );
      
      const fetchedTerms = res.data?.terms || [];
      setTerms(fetchedTerms);
      log(`Fetched ${fetchedTerms.length} terms for AY ${academicYearId}`);
      return fetchedTerms;
    } catch (err) {
      console.warn('Error fetching terms:', err);
      setTerms([]);
      return [];
    } finally {
      setLoadingContexts(false);
    }
  }, [tenantId, log]);

  // Handle academic year selection - this will trigger context loading
  useEffect(() => {
    const loadContextsForAY = async () => {
      if (!selectedAcademicYear) {
        setSemesters([]);
        setTerms([]);
        setIsSemesterBased(false);
        return;
      }

      const ay = academicYears.find(y => y._id === selectedAcademicYear);
      if (!ay) return;

      setIsSemesterBased(ay.isSemesterBased);
      
      // Clear previous selections
      setSelectedSemester('');
      setSelectedTerm('');
      
      // Load appropriate contexts
      if (ay.isSemesterBased) {
        await fetchSemesters(selectedAcademicYear);
      } else {
        await fetchTerms(selectedAcademicYear);
      }
    };

    loadContextsForAY();
  }, [selectedAcademicYear, academicYears, fetchSemesters, fetchTerms]);

  // Initial fetch
  useEffect(() => {
    fetchAllAcademicYears();
  }, [fetchAllAcademicYears]);

  const getAcademicYearById = useCallback((id) => {
    return academicYears.find(ay => ay._id === id) || null;
  }, [academicYears]);

  const isAcademicYearCompleted = useCallback((id) => {
    return completedAcademicYears.some(ay => ay._id === id);
  }, [completedAcademicYears]);

  const getSemesterName = useCallback((semesterId) => {
    if (!semesterId) return '';
    const semester = semesters.find(s => s._id === semesterId);
    return semester ? (semester.semesterName || `Semester ${semester.semesterNumber}`) : '';
  }, [semesters]);

  const getTermName = useCallback((termId) => {
    if (!termId) return '';
    const term = terms.find(t => t._id === termId);
    return term ? (term.termLabel || `Term ${term.termNumber}`) : '';
  }, [terms]);

  const getCurrentContexts = useCallback(() => {
    const ay = getAcademicYearById(selectedAcademicYear);
    if (!ay) return [];
    if (ay.isSemesterBased) return semesters;
    return terms;
  }, [selectedAcademicYear, getAcademicYearById, semesters, terms]);

  const getCurrentContextName = useCallback((contextId) => {
    const ay = getAcademicYearById(selectedAcademicYear);
    if (!ay || !contextId) return '';
    if (ay.isSemesterBased) {
      return getSemesterName(contextId);
    }
    return getTermName(contextId);
  }, [selectedAcademicYear, getAcademicYearById, getSemesterName, getTermName]);

  return {
    academicYears,
    activeAcademicYears,
    completedAcademicYears,
    loading,
    loadingContexts,
    error,
    selectedAcademicYear,
    setSelectedAcademicYear,
    selectedSemester,
    setSelectedSemester,
    selectedTerm,
    setSelectedTerm,
    semesters,
    terms,
    isSemesterBased,
    refresh: fetchAllAcademicYears,
    getAcademicYearById,
    isAcademicYearCompleted,
    getSemesterName,
    getTermName,
    getCurrentContexts,
    getCurrentContextName,
    fetchSemesters,
    fetchTerms
  };
};

export default useAcademicData;